import { useEffect, useState, useCallback } from "react";
import {
    MessageCircle,
    Phone,
    Star,
    Check,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { CONTACT, WHATSAPP_URL, TEL_URL, IMAGES } from "../data/site";

// Slides — mix of services + salon photos
const SLIDES = [
    {
        image: "exterior",
        label: "Cegu Masaj · Batı Palas Özlüce",
        title: "Bursa Özlüce'de",
        titleHighlight: "Konforlu",
        titleAfter: "Masaj Salonu",
        text:
            "Batı Palas içerisinde, uzman terapistler ve hijyenik salon ortamıyla size özel bir wellness deneyimi.",
    },
    {
        image: "heroSpa",
        label: "Cegu Masaj · Bursa Özlüce",
        title: "Bursa Özlüce'de",
        titleHighlight: "Profesyonel",
        titleAfter: "Masaj Deneyimi",
        text:
            "Uzman terapistler, hijyenik salon ortamı ve size özel masaj seçenekleriyle bedeninizi dinlendirin.",
    },
    {
        image: "sultan",
        label: "Sultan Masajı · 90 dk",
        title: "4 El",
        titleHighlight: "Sultan",
        titleAfter: "Masajı",
        text:
            "Cegu imzalı 4-el sultan masajı ile derin bir rahatlama. Uzman terapistlerimizden özel deneyim.",
    },
    {
        image: "thai",
        label: "Thai Masajı · 75 dk",
        title: "Uzakdoğu",
        titleHighlight: "Thai",
        titleAfter: "Masajı",
        text:
            "Geleneksel Thai teknikleri, esneme ve akupresürle bütünsel bir yenilenme.",
    },
    {
        image: "jakuzi",
        label: "Jakuzi Salonumuz",
        title: "Özel",
        titleHighlight: "Jakuzi",
        titleAfter: "Salonumuz",
        text:
            "Isıtılmış termal su, aromaterapi mumları ve size özel dinlenme atmosferi.",
    },
    {
        image: "corridor",
        label: "Salonumuz · Batı Palas",
        title: "Konforlu",
        titleHighlight: "Salon",
        titleAfter: "Ortamı",
        text:
            "Ferah koridorlar, hijyenik özel odalar. Bursa Özlüce Batı Palas içerisinde.",
    },
];

const AUTOPLAY = 6000;

export default function Hero() {
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);

    const next = useCallback(() => setIdx((i) => (i + 1) % SLIDES.length), []);
    const prev = useCallback(
        () => setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length),
        []
    );

    useEffect(() => {
        if (paused) return;
        const t = setInterval(next, AUTOPLAY);
        return () => clearInterval(t);
    }, [next, paused]);

    // Preload images
    useEffect(() => {
        SLIDES.forEach((s) => {
            const img = new Image();
            img.src = IMAGES[s.image];
        });
    }, []);

    return (
        <section
            id="hero"
            data-testid="hero-section"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            className="relative w-full bg-[#0a0a0c] overflow-hidden pt-[64px] md:pt-[80px]"
        >
            <div className="relative w-full h-[calc(100svh-64px)] min-h-[560px] max-h-[820px] md:h-[calc(100vh-80px)] md:min-h-[600px] md:max-h-[900px]">
                {/* Slides */}
                {SLIDES.map((slide, i) => (
                    <div
                        key={i}
                        aria-hidden={i !== idx}
                        data-testid={`hero-slide-${i}`}
                        className={`absolute inset-0 transition-opacity duration-[900ms] ease-out ${
                            i === idx ? "opacity-100 z-10" : "opacity-0 z-0"
                        }`}
                    >
                        <img
                            src={IMAGES[slide.image]}
                            alt={slide.label}
                            className="w-full h-full object-cover"
                            loading={i === 0 ? "eager" : "lazy"}
                            fetchpriority={i === 0 ? "high" : "auto"}
                        />
                        {/* Black gradient masking (bottom + left) */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c]/70 to-[#0a0a0c]/25" />
                        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0c]/85 via-[#0a0a0c]/50 to-transparent" />
                        {/* Gold radial glow accent */}
                        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#c9a24b]/15 blur-[130px] pointer-events-none" />
                    </div>
                ))}

                {/* Content overlay */}
                <div className="absolute inset-0 z-20 flex items-end md:items-center">
                    <div className="container-x w-full pb-24 md:pb-16">
                        <div className="max-w-2xl">
                            {/* Slide-dependent content — animated on change */}
                            <div key={idx} className="animate-fadeUp">
                                <div className="chip mb-5" data-testid="hero-eyebrow">
                                    <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#f5d685] to-[#c9a24b]" />
                                    {SLIDES[idx].label}
                                </div>

                                <h1
                                    data-testid="hero-headline"
                                    className="text-[2.15rem] sm:text-[2.75rem] md:text-[3.25rem] lg:text-[3.75rem] xl:text-[4rem] font-bold leading-[1.05] tracking-[-0.025em] text-[#f4efe4]"
                                >
                                    {SLIDES[idx].title}{" "}
                                    <span className="gold-text">
                                        {SLIDES[idx].titleHighlight}
                                    </span>{" "}
                                    {SLIDES[idx].titleAfter}
                                </h1>

                                <p
                                    data-testid="hero-subtitle"
                                    className="mt-5 text-[14.5px] md:text-[16px] leading-relaxed text-[#d5cebd] max-w-xl"
                                >
                                    {SLIDES[idx].text}
                                </p>
                            </div>

                            {/* Trust bullets */}
                            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-[#d5cebd]">
                                {[
                                    "Uzman Terapistler",
                                    "Hijyenik Ortam",
                                    "Kolay Randevu",
                                ].map((t) => (
                                    <li
                                        key={t}
                                        className="inline-flex items-center gap-1.5"
                                    >
                                        <Check className="w-3.5 h-3.5 text-[#c9a24b]" />
                                        {t}
                                    </li>
                                ))}
                            </ul>

                            {/* CTAs */}
                            <div className="mt-7 flex flex-col sm:flex-row gap-3">
                                <a
                                    href={WHATSAPP_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-gold flex-1 sm:flex-none !min-w-[220px]"
                                    data-testid="hero-cta-whatsapp"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    WhatsApp'tan Randevu Al
                                </a>
                                <a
                                    href={TEL_URL}
                                    className="btn btn-ghost flex-1 sm:flex-none"
                                    data-testid="hero-cta-call"
                                >
                                    <Phone className="w-4 h-4" />
                                    Hemen Ara
                                </a>
                            </div>

                            {/* Rating row */}
                            <div className="mt-8 flex items-center gap-4 md:gap-6 flex-wrap">
                                <div className="flex items-center gap-2">
                                    <div className="flex">
                                        {[...Array(5)].map((_, i) => (
                                            <Star
                                                key={i}
                                                className="w-3.5 h-3.5 star"
                                            />
                                        ))}
                                    </div>
                                    <span className="text-[13px] font-semibold text-[#f4efe4]">
                                        4.9
                                    </span>
                                    <span className="text-[12px] text-[#a49d8f]">
                                        / Google
                                    </span>
                                </div>
                                <span className="w-px h-4 bg-white/15" />
                                <div className="text-[13px] text-[#d5cebd]">
                                    <span className="text-[#f4efe4] font-semibold">
                                        500+
                                    </span>{" "}
                                    Mutlu Misafir
                                </div>
                                <span className="w-px h-4 bg-white/15 hidden sm:block" />
                                <div className="text-[13px] text-[#d5cebd] hidden sm:block">
                                    <span className="text-[#f4efe4] font-semibold">
                                        10:00 → 24:00
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Nav arrows — hidden on mobile */}
                <button
                    onClick={prev}
                    aria-label="Önceki"
                    data-testid="hero-prev"
                    className="hidden md:flex absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-black/40 backdrop-blur-md hover:bg-[#c9a24b] hover:text-[#0a0a0c] border border-white/15 text-white items-center justify-center transition-all"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                    onClick={next}
                    aria-label="Sonraki"
                    data-testid="hero-next"
                    className="hidden md:flex absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-black/40 backdrop-blur-md hover:bg-[#c9a24b] hover:text-[#0a0a0c] border border-white/15 text-white items-center justify-center transition-all"
                >
                    <ChevronRight className="w-5 h-5" />
                </button>

                {/* Dots + slide counter (bottom) */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-4">
                    <div
                        className="flex items-center gap-2"
                        data-testid="hero-dots"
                    >
                        {SLIDES.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setIdx(i)}
                                aria-label={`Slayt ${i + 1}`}
                                data-testid={`hero-dot-${i}`}
                                className={`transition-all rounded-full ${
                                    i === idx
                                        ? "w-8 h-1.5 bg-gradient-to-r from-[#f5d685] to-[#c9a24b]"
                                        : "w-1.5 h-1.5 bg-white/40 hover:bg-white/70"
                                }`}
                            />
                        ))}
                    </div>
                    <span className="text-[11px] text-white/60 tabular-nums font-medium">
                        <span className="text-[#f5d685]">
                            {String(idx + 1).padStart(2, "0")}
                        </span>
                        {" / "}
                        {String(SLIDES.length).padStart(2, "0")}
                    </span>
                </div>

                {/* Progress bar (auto-play indicator) */}
                <div className="absolute bottom-0 inset-x-0 h-[2px] bg-white/5 z-30">
                    <div
                        key={idx + (paused ? "-p" : "")}
                        className="h-full bg-gradient-to-r from-[#f5d685] to-[#c9a24b]"
                        style={{
                            animation: paused
                                ? "none"
                                : `heroProgress ${AUTOPLAY}ms linear forwards`,
                        }}
                    />
                </div>
            </div>

            <style>{`
                @keyframes heroProgress {
                    from { width: 0%; }
                    to { width: 100%; }
                }
                @keyframes fadeUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeUp {
                    animation: fadeUp 0.9s cubic-bezier(0.2, 0.9, 0.2, 1) both;
                }
            `}</style>
        </section>
    );
}
